import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { DateInput, VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { ReportTextCell, todayIso, CrewDataTableFrame, CrewDataTablePrompt, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import type { WageAllotteeResponse, WageAllotteeRow } from './types';

const WageAllotteePage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [vesselId, setVesselId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(todayIso);
  const [periodTo, setPeriodTo] = useState(todayIso);
  const [applied, setApplied] = useState<{ vesselId: string; periodFrom: string; periodTo: string } | null>(
    null,
  );

  const listUrl = applied
    ? `/crew/reports/wage-allottee?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<WageAllotteeResponse>(listUrl, fetcher, {
    revalidateOnFocus: false});

  const columns: Column<WageAllotteeRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link to={`/crew/${row.crewId}`} state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)} onClick={(e) => e.stopPropagation()}>
            {row.name}
          </Link>
        )},
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'status', header: 'Status', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'wage',
        header: 'Contract wage',
        sortable: true,
        render: (_v, row) => (
          <ReportTextCell
            value={
              row.wage == null
                ? null
                : `${row.wage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${
                    row.wageCurrency ? ` ${row.wageCurrency}` : ''
                  }`
            }
          />
        )},
      { key: 'allotteeCount', header: 'Allottees', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'allotteeAmount',
        header: 'Allottee total',
        sortable: true,
        render: (v) => (
          <ReportTextCell
            value={
              typeof v === 'number'
                ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : v
            }
          />
        )},
    ],
    [],
  );

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setApplied({ vesselId, periodFrom, periodTo });
  }, [vesselId, periodFrom, periodTo]);

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'Wage / allottee summary',
          body: 'Contract wages for crew assigned to the vessel in the period, plus standing allottee counts and amounts from the crew file. Period payroll remittance remains in MGA.'}}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={applied ? () => mutate() : undefined}
        context={
          data?.vessel
            ? `${data.vessel.vesselName} · ${data.periodFrom} → ${data.periodTo} · ${data.summary.crewCount} crew · wage ${data.summary.totalWage.toLocaleString(undefined, { maximumFractionDigits: 2 })} · ${data.summary.allotteeCount} allottees`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period from" width="md">
          <DateInput name="periodFrom" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period to" width="md">
          <DateInput name="periodTo" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!applied ? (
          <CrewDataTablePrompt>Select a vessel and period, then search.
          </CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt><span className="text-red-600">Could not load wage summary.</span></CrewDataTablePrompt>
        ) : (
          <DataTable
            data={data?.rows ?? []}
            columns={columns}
            keyExtractor={(row) => row.crewId}
            loading={isLoading}
            emptyMessage="No crew in period"
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>
    </div>
  );
};

export default WageAllotteePage;
